import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import TextInput from 'elements/TextInput';
import { Button } from 'elements/Button';
import classNames from 'classnames';

interface FormValues {
  name: string;
}

interface Props {
  email: string;
  onBack: () => void;
  onSubmit: (name: string) => Promise<void>;
}

const NameForm: React.FC<Props> = ({ email, onBack, onSubmit }) => {
  const t = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  return (
    <form
      onSubmit={handleSubmit(({ name }) => onSubmit(name))}
      className={styles.container}
    >
      <button type="button" onClick={onBack} className={styles.backButton}>
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        {t('common.back')}
      </button>
      <div className={styles.header}>
        <p className={styles.title}>{t('auth.name.title')}</p>
        <p className={styles.subtitle}>
          {t.rich('auth.name.subtitle', {
            email,
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </p>
        <p className={styles.subtitle}>{t('auth.name.hint')}</p>
      </div>
      <TextInput
        name="name"
        label="Name"
        hideLabel
        type="text"
        placeholder="Your display name"
        autoComplete="off"
        autoFocus
        errors={errors}
        rootClassName={styles.root}
        inputClassName={styles.input}
        {...register('name', { required: true })}
      />
      <Button
        type="submit"
        variant="solid"
        color="neutral"
        size="md"
        fullWidth
        loading={isSubmitting}
        loadingText={t('auth.name.sending')}
        className="text-sm font-semibold"
      >
        {t('common.continue')}
      </Button>
    </form>
  );
};

export default NameForm;

const styles = {
  container: 'flex flex-col w-72 space-y-4 text-left',
  header: 'space-y-4',
  title: 'text-lg font-semibold text-mf-off-black-950',
  subtitle: 'text-sm text-mf-off-black-500',
  backButton: classNames(
    'self-start flex items-center gap-1.5 text-sm text-mf-gray-500 hover:text-mf-gray-700',
    'transition-colors duration-150',
  ),
  // The app's field reshaped into a white pill. Colours are pinned so it
  // stays white-on-light even when the app's theme class is dark.
  root: classNames(
    'rounded-full border-mf-gray-200 bg-white px-5 text-sm text-mf-off-black-600 shadow-none',
    'transition-all duration-200 focus-within:border-mf-blue-400 focus-within:ring-blue-100',
  ),
  input: 'placeholder:text-[#A4AEC2]',
};
